import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ROLE_LABELS } from "@/tours/guides";

const UNIT_HINT = { "%": "%", ratio: "", days: "days", INR: "₹" };
const toInput = (v) => (v === null || v === undefined ? "" : String(v));
const fromInput = (v) => (v === "" ? null : Number(v));

/** Administration → Alert thresholds: per KPI, when it turns amber / red, who owns it, and whether it is worked out at all.
 *  Changes apply to this organisation only and take effect at the next run. */
export default function KpiSettings() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);
  const [failed, setFailed] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/kpis/settings");
      setData(d);
      setDraft(Object.fromEntries(d.kpis.map((k) => [k.code, { amber: toInput(k.amber_threshold), red: toInput(k.red_threshold), owner_role: k.owner_role, is_active: k.is_active }])));
      setFailed(null);
    } catch (err) { setFailed(getErrorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changed = (k) => {
    const d = draft[k.code];
    return d && (d.amber !== toInput(k.amber_threshold) || d.red !== toInput(k.red_threshold) || d.owner_role !== k.owner_role || d.is_active !== k.is_active);
  };
  const set = (code, field, value) => setDraft((d) => ({ ...d, [code]: { ...d[code], [field]: value } }));

  const save = async (k) => {
    const d = draft[k.code];
    setSaving(k.code);
    try {
      const { data: row } = await api.put(`/kpis/settings/${k.code}`, { amber_threshold: fromInput(d.amber), red_threshold: fromInput(d.red), owner_role: d.owner_role, is_active: d.is_active });
      setData((x) => ({ ...x, kpis: x.kpis.map((y) => (y.code === k.code ? row : y)) }));
      toast.success(`${k.name}: saved. It applies from the next run.`);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(null); }
  };
  const reset = async (k) => {
    setSaving(k.code);
    try {
      const { data: row } = await api.delete(`/kpis/settings/${k.code}`);
      setData((x) => ({ ...x, kpis: x.kpis.map((y) => (y.code === k.code ? row : y)) }));
      setDraft((dr) => ({ ...dr, [k.code]: { amber: toInput(row.amber_threshold), red: toInput(row.red_threshold), owner_role: row.owner_role, is_active: row.is_active } }));
      toast.success(`${k.name}: back to the default.`);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(null); }
  };

  if (failed) return <p className="text-sm text-destructive" role="alert">{failed} <Button variant="link" onClick={load}>Try again</Button></p>;
  if (!data) return <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>;
  const s = data.schedule;

  return (
    <div className="space-y-5" data-testid="kpi-settings">
      <PageHeader title="Alert thresholds" description="When each indicator turns amber or red, who it belongs to, and whether it is worked out. These apply to your organisation only." />

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="font-semibold text-slate-800 dark:text-slate-100">When</div>
          <p className="mt-1 text-muted-foreground" data-testid="kpi-schedule">
            {s.enabled ? <>Worked out every night at {String(s.run_hour).padStart(2, "0")}:00; amber digest at {String(s.digest_hour).padStart(2, "0")}:00 ({s.timezone}).</>
              : <>The nightly run is <b>off</b> on this server (ENABLE_SCHEDULER). Use “Work out now” on the Performance page.</>}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="font-semibold text-slate-800 dark:text-slate-100">Who hears about it</div>
          <p className="mt-1 text-muted-foreground">The owner role below, the project’s Project Managers, and Admins for red. Only people who can see that project and module.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="font-semibold text-slate-800 dark:text-slate-100">How</div>
          <p className="mt-1 text-muted-foreground" data-testid="kpi-email">
            Red: an e-mail and the bell, once per alert. Amber: one digest e-mail each morning.
            {!s.email_configured && <> <b>E-mail is not set up</b> on this server (SMTP), so only the bell is used.</>}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-[#ECF4F7] text-left text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <tr>
              <th className="px-4 py-2.5 font-semibold">Indicator</th>
              <th className="px-3 py-2.5 font-semibold">Amber when</th>
              <th className="px-3 py-2.5 font-semibold">Red when</th>
              <th className="px-3 py-2.5 font-semibold">Owner</th>
              <th className="px-3 py-2.5 font-semibold">On</th>
              <th className="w-48 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {data.kpis.map((k) => {
              const d = draft[k.code] || {};
              const word = k.direction === "lower_is_better" ? "above" : "below";
              return (
                <tr key={k.code} className="border-t border-slate-100 align-top dark:border-slate-800" data-testid={`kpi-setting-${k.code}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 dark:text-slate-100">{k.name}</div>
                    <div className="max-w-md text-xs text-muted-foreground">{k.covers}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{k.direction === "lower_is_better" ? "Lower is better" : "Higher is better"}{k.scope === "COMPANY" ? " · per company" : " · per project"}{k.customised && <span className="ml-1 font-medium text-[#910029] dark:text-rose-300">· changed from default</span>}</div>
                  </td>
                  {["amber", "red"].map((f) => (
                    <td key={f} className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">{word}</span>
                        <Input type="number" step="any" className="h-8 w-24" value={d[f] ?? ""} placeholder="none" onChange={(e) => set(k.code, f, e.target.value)}
                          aria-label={`${k.name} ${f} threshold`} data-testid={`${f}-${k.code}`} />
                        <span className="text-xs text-muted-foreground">{UNIT_HINT[k.unit] ?? k.unit}</span>
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">default {k.default[`${f}_threshold`] ?? "none"}</div>
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    <Select value={d.owner_role} onValueChange={(v) => set(k.code, "owner_role", v)}>
                      <SelectTrigger className="h-8 w-40" aria-label={`${k.name} owner`}><SelectValue /></SelectTrigger>
                      <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-3"><Switch checked={!!d.is_active} onCheckedChange={(v) => set(k.code, "is_active", v)} aria-label={`${k.name} on`} /></td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {k.customised && <Button variant="ghost" size="sm" disabled={saving === k.code} onClick={() => reset(k)} title="Back to the default"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Default</Button>}
                    <Button size="sm" disabled={!changed(k) || saving === k.code} onClick={() => save(k)} data-testid={`save-${k.code}`}>
                      {saving === k.code ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Save
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {Object.keys(data.not_available || {}).length > 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-muted-foreground dark:border-slate-700">
          <div className="font-medium text-slate-700 dark:text-slate-200">Not available yet</div>
          <ul className="mt-1 list-disc pl-5">{Object.entries(data.not_available).map(([c, why]) => <li key={c}><b>{c.replaceAll("_", " ").toLowerCase()}</b>: {why}</li>)}</ul>
        </div>
      )}
      <p className="text-xs text-muted-foreground">See the results on <Link to="/performance" className="underline">Performance</Link>.</p>
    </div>
  );
}
